'use client'

import { useEffect } from 'react'

/**
 * Runtime client de interatividade do checkout transparente dos templates de vendas
 * (/livro, /combo, /audio, /curso). Faz:
 *
 * 1. Plan-radio change (`input[name="plan"]` dentro de `.checkout-grid`):
 *    - Atualiza `form.action` => /api/checkout/offer/{offerId}
 *    - Atualiza textos do resumo (#order-name, #order-price, #order-subtotal,
 *      #order-discount, #order-total, #order-installments)
 *    - Atualiza classe `is-active` nos labels `.plan-radio`
 *    - Atualiza opções de parcelas em `#ck-card-inst`
 *
 * 2. CEP autocomplete (#ck-cep blur):
 *    - Consulta ViaCEP e preenche #ck-street, #ck-district, #ck-city, #ck-state
 *
 * 3. Pay-tabs (cartão/PIX/boleto):
 *    - Alterna `is-active` nos buttons e troca o painel visível
 *    - Atualiza `input[name="billingType"]` (hidden)
 *
 * Renderiza null. O DOM é puramente do template HTML.
 */
export function CheckoutRuntime() {
  useEffect(() => {
    const form = document.querySelector<HTMLFormElement>('.checkout-grid')
    if (!form) return

    // ====================================================================
    // 1. PLAN RADIO
    // ====================================================================
    const planRadios = form.querySelectorAll<HTMLInputElement>('input[name="plan"]')
    const orderName = document.getElementById('order-name')
    const orderPrice = document.getElementById('order-price')
    const orderSubtotal = document.getElementById('order-subtotal')
    const orderDiscount = document.getElementById('order-discount')
    const orderTotal = document.getElementById('order-total')
    const orderInstallments = document.getElementById('order-installments')
    const ckCardInst = document.getElementById('ck-card-inst') as HTMLSelectElement | null
    const billingHidden = form.querySelector<HTMLInputElement>('input[name="billingType"]')

    function fmt(value: number): string {
      return 'R$ ' + value.toFixed(2).replace('.', ',')
    }

    function applyPlan(radio: HTMLInputElement) {
      const label = radio.closest('.plan-radio') as HTMLLabelElement | null
      if (!label) return
      const offerId = label.dataset.offerId
      const offerPrice = parseFloat(label.dataset.offerPrice || '0')
      const offerName = label.dataset.offerName || ''
      const oldPriceStr = label.dataset.old || ''
      const installmentsStr = label.dataset.installments || ''

      // 1a. Update form action
      if (offerId) {
        form.setAttribute('action', `/api/checkout/offer/${offerId}`)
      }

      // 1b. Update resumo do pedido
      if (offerPrice > 0) {
        const priceStr = fmt(offerPrice)
        orderPrice && (orderPrice.textContent = priceStr)
        orderTotal && (orderTotal.textContent = priceStr)
        // Subtotal: preço antigo (se houver) senão preço atual
        const subtotalNum = oldPriceStr ? parseFloat(oldPriceStr.replace(',', '.')) : offerPrice
        orderSubtotal && (orderSubtotal.textContent = fmt(subtotalNum))
        // Desconto: subtotal - preço atual
        const discountNum = subtotalNum - offerPrice
        if (orderDiscount) {
          if (discountNum > 0) {
            orderDiscount.textContent = '- ' + fmt(discountNum)
          } else {
            orderDiscount.textContent = 'R$ 0,00'
          }
        }
        // Parcelas
        orderInstallments && installmentsStr && (orderInstallments.textContent = 'ou ' + installmentsStr + ' sem juros')
      }

      // 1c. Update order-name / order-meta
      if (orderName && offerName) {
        orderName.textContent = offerName
      }

      // 1d. is-active nos labels
      form.querySelectorAll('.plan-radio').forEach((l) => {
        l.classList.toggle('is-active', l.contains(radio))
      })

      // 1e. Regenera opções de parcelas do cartão (1× a 6× sem juros)
      if (ckCardInst && offerPrice > 0) {
        const previousValue = ckCardInst.value
        ckCardInst.innerHTML = ''
        for (let n = 1; n <= 6; n++) {
          const optValue = offerPrice / n
          const opt = document.createElement('option')
          opt.textContent = `${n}× ${fmt(optValue)} sem juros`
          if (
            (previousValue && previousValue.startsWith(`${n}×`)) ||
            (!previousValue && n === 1)
          ) {
            opt.selected = true
          }
          ckCardInst.appendChild(opt)
        }
      }
    }

    const onPlanChange = (e: Event) => {
      const t = e.target as HTMLInputElement
      if (t && t.checked) applyPlan(t)
    }
    planRadios.forEach((r) => r.addEventListener('change', onPlanChange))

    // Click no LABEL inteiro também aplica (UX: alguns browsers só disparam change)
    const onLabelClick = (ev: Event) => {
      const label = (ev.currentTarget as HTMLElement)
      const radio = label.querySelector<HTMLInputElement>('input[name="plan"]')
      if (radio && !radio.checked) {
        radio.checked = true
        applyPlan(radio)
      }
    }
    const labels = Array.from(form.querySelectorAll<HTMLLabelElement>('.plan-radio'))
    labels.forEach((l) => l.addEventListener('click', onLabelClick))

    // Inicializa: aplica o plan já selecionado (ou primeiro)
    const checked = Array.from(planRadios).find((r) => r.checked) || planRadios[0]
    if (checked) applyPlan(checked)

    // ====================================================================
    // 2. CEP autocomplete (ViaCEP)
    // ====================================================================
    const cepInput = document.getElementById('ck-cep') as HTMLInputElement | null
    const onCepBlur = async () => {
      if (!cepInput) return
      const cep = (cepInput.value || '').replace(/\D/g, '')
      if (cep.length !== 8) return
      try {
        const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
        if (!r.ok) return
        const d = await r.json()
        if (!d || d.erro) return
        const street = document.getElementById('ck-street') as HTMLInputElement | null
        const district = document.getElementById('ck-district') as HTMLInputElement | null
        const city = document.getElementById('ck-city') as HTMLInputElement | null
        const state = document.getElementById('ck-state') as HTMLSelectElement | null
        if (street && !street.value && d.logradouro) street.value = d.logradouro
        if (district && !district.value && d.bairro) district.value = d.bairro
        if (city && !city.value && d.localidade) city.value = d.localidade
        if (state && d.uf) state.value = d.uf
      } catch {
        // silent fail
      }
    }
    cepInput?.addEventListener('blur', onCepBlur)

    // Formata CEP visualmente
    const onCepInput = () => {
      if (!cepInput) return
      const raw = cepInput.value.replace(/\D/g, '').slice(0, 8)
      cepInput.value = raw.length > 5 ? `${raw.slice(0, 5)}-${raw.slice(5)}` : raw
    }
    cepInput?.addEventListener('input', onCepInput)

    // ====================================================================
    // 3. Pay-tabs (cartão / PIX / boleto)
    // ====================================================================
    const payTabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.pay-tab'))
    const payPanels = Array.from(document.querySelectorAll<HTMLElement>('.payment-panel'))
    const onPayTab = (e: Event) => {
      const btn = e.currentTarget as HTMLButtonElement
      const method = btn.dataset.method
      if (!method) return
      payTabs.forEach((t) => {
        const active = t === btn
        t.classList.toggle('is-active', active)
        t.setAttribute('aria-selected', String(active))
      })
      payPanels.forEach((p) => {
        p.hidden = p.dataset.panel !== method
      })
      // Atualiza billingType
      if (billingHidden) {
        const map: Record<string, string> = {
          card: 'CREDIT_CARD',
          pix: 'PIX',
          boleto: 'BOLETO',
        }
        billingHidden.value = map[method] || 'PIX'
      }
    }
    payTabs.forEach((b) => b.addEventListener('click', onPayTab))

    // ====================================================================
    // 4. PIX transparente (intercepta submit do form, mostra QR Code direto)
    // ====================================================================

    // Injeta estilos minimos pro estado transparente (PIX + cartão + boleto)
    const styleEl = document.createElement('style')
    styleEl.dataset.checkoutPix = 'true'
    styleEl.textContent = `
      /* Estado-machine usado por PIX, cartão e boleto. !important pra vencer
         .pix-state-grid {display:grid} que vem depois no CSS. */
      .pix-state-loading, .pix-state-ready, .pix-state-error,
      .card-state-loading, .card-state-success, .card-state-error,
      .boleto-state-loading, .boleto-state-ready, .boleto-state-error { display: none !important; }
      .pix-display[data-state="loading"] .pix-state-idle,
      .pix-display[data-state="ready"]   .pix-state-idle,
      .pix-display[data-state="error"]   .pix-state-idle { display: none !important; }
      .pix-display[data-state="loading"] .pix-state-loading,
      .pix-display[data-state="ready"]   .pix-state-ready,
      .pix-display[data-state="error"]   .pix-state-error { display: grid !important; }
      .boleto-info[data-state="loading"] .boleto-state-idle,
      .boleto-info[data-state="ready"]   .boleto-state-idle,
      .boleto-info[data-state="error"]   .boleto-state-idle { display: none !important; }
      .boleto-info[data-state="loading"] .boleto-state-loading,
      .boleto-info[data-state="ready"]   .boleto-state-ready,
      .boleto-info[data-state="error"]   .boleto-state-error { display: grid !important; }
      .payment-panel[data-panel="card"][data-state="loading"] [data-card-form],
      .payment-panel[data-panel="card"][data-state="success"] [data-card-form],
      .payment-panel[data-panel="card"][data-state="error"] [data-card-form] { display: none !important; }
      .payment-panel[data-panel="card"][data-state="loading"] .card-state-loading,
      .payment-panel[data-panel="card"][data-state="success"] .card-state-success,
      .payment-panel[data-panel="card"][data-state="error"] .card-state-error { display: grid !important; }
      .pix-qr-image { width: 220px; height: 220px; object-fit: contain; background: #fff; border-radius: 8px; padding: 8px; }
      .pix-copy-row { display: flex; gap: 8px; align-items: stretch; margin-top: 8px; }
      .pix-copy-row input { flex: 1; padding: 10px 12px; border: 1px solid var(--border, #ddd); border-radius: 8px; font-family: monospace; font-size: 12px; }
      .pix-copy-row button { padding: 10px 16px; border: 0; border-radius: 8px; background: var(--brand-primary, #1E2A6B); color: #fff; font-weight: 600; cursor: pointer; white-space: nowrap; }
      .pix-copy-row button:hover { filter: brightness(1.1); }
      .pix-copy-row button.copied { background: #16a34a; }
      .pix-loading-spin { width: 28px; height: 28px; border: 3px solid var(--border, #eee); border-top-color: var(--brand-primary, #1E2A6B); border-radius: 50%; animation: pix-spin 0.8s linear infinite; }
      @keyframes pix-spin { to { transform: rotate(360deg); } }
      .pix-state-grid { display: grid; gap: 16px; justify-items: center; text-align: center; padding: 12px; }
      .pix-state-grid strong { font-size: 16px; }
      .pix-state-grid p { font-size: 14px; color: var(--fg2, #555); margin: 0; max-width: 320px; }
      .checkout-btn-secondary { padding: 12px 22px; border-radius: 10px; border: 1px solid var(--border, #ddd); background: transparent; cursor: pointer; font-weight: 500; }
      .checkout-btn-primary { padding: 12px 26px; border-radius: 10px; border: 0; background: var(--brand-primary, #1E2A6B); color: #fff; cursor: pointer; font-weight: 600; }
      .checkout-btn-primary:hover { filter: brightness(1.1); }
      .success-check { width: 56px; height: 56px; border-radius: 50%; background: #16a34a; color: #fff; display: grid; place-items: center; font-size: 32px; line-height: 1; }
      .error-x { width: 56px; height: 56px; border-radius: 50%; background: #dc2626; color: #fff; display: grid; place-items: center; font-size: 32px; line-height: 1; }
    `
    document.head.appendChild(styleEl)

    const pixPanel = document.querySelector<HTMLElement>('.payment-panel[data-panel="pix"]')
    const pixDisplay = pixPanel?.querySelector<HTMLElement>('.pix-display')
    let pixOriginalHTML = ''
    if (pixDisplay) {
      pixOriginalHTML = pixDisplay.innerHTML
      // Estado inicial = idle (mantém HTML original como estado idle)
      pixDisplay.dataset.state = 'idle'
      pixDisplay.classList.add('pix-display-transparent')
      // Wrap original em .pix-state-idle
      pixDisplay.innerHTML = `
        <div class="pix-state-idle">
          ${pixOriginalHTML}
        </div>
        <div class="pix-state-loading pix-state-grid">
          <div class="pix-loading-spin"></div>
          <strong>Gerando PIX…</strong>
          <p>Estamos criando seu QR Code. Aguarde alguns segundos.</p>
        </div>
        <div class="pix-state-ready pix-state-grid">
          <img class="pix-qr-image" alt="QR Code do PIX" data-pix-qr />
          <strong>Aponte a câmera do seu app do banco</strong>
          <p>Ou copie o código abaixo e cole no campo "Pix copia e cola" do seu banco.</p>
          <div class="pix-copy-row" style="width:100%;max-width:420px;">
            <input type="text" readonly data-pix-payload value="" aria-label="Código PIX copia e cola" />
            <button type="button" data-pix-copy>Copiar</button>
          </div>
          <p data-pix-expiration style="font-size:12px;color:var(--fg3,#888)"></p>
        </div>
        <div class="pix-state-error pix-state-grid">
          <strong style="color:#dc2626">Não foi possível gerar o PIX</strong>
          <p data-pix-error-msg>Tente novamente em alguns segundos ou troque para cartão/boleto.</p>
          <button type="button" data-pix-retry style="padding:10px 16px;border:1px solid var(--border,#ddd);border-radius:8px;background:transparent;cursor:pointer">Tentar de novo</button>
        </div>
      `
    }

    // ==================================================================
    // Máscaras visuais nos inputs do cartão
    // ==================================================================
    const cardNumberInput = form.querySelector<HTMLInputElement>('input[name="cardNumber"]')
    const cardExpInput = form.querySelector<HTMLInputElement>('input[name="cardExp"]')
    const cardCvvInput = form.querySelector<HTMLInputElement>('input[name="cardCvv"]')

    const onCardNumberInput = () => {
      if (!cardNumberInput) return
      const raw = cardNumberInput.value.replace(/\D/g, '').slice(0, 19)
      cardNumberInput.value = raw.replace(/(\d{4})(?=\d)/g, '$1 ')
    }
    cardNumberInput?.addEventListener('input', onCardNumberInput)

    const onCardExpInput = () => {
      if (!cardExpInput) return
      let raw = cardExpInput.value.replace(/\D/g, '').slice(0, 4)
      if (raw.length >= 3) raw = raw.slice(0, 2) + '/' + raw.slice(2)
      cardExpInput.value = raw
    }
    cardExpInput?.addEventListener('input', onCardExpInput)

    const onCardCvvInput = () => {
      if (!cardCvvInput) return
      cardCvvInput.value = cardCvvInput.value.replace(/\D/g, '').slice(0, 4)
    }
    cardCvvInput?.addEventListener('input', onCardCvvInput)

    // ==================================================================
    // CARTÃO transparente — estados visuais (loading / success / error)
    // ==================================================================
    const cardPanel = document.querySelector<HTMLElement>('.payment-panel[data-panel="card"]')
    if (cardPanel) {
      // Marca o miolo de inputs como [data-card-form] para esconder em loading/sucesso/erro
      const cardInputs = cardPanel.querySelectorAll('.form-row, .form-field')
      cardInputs.forEach((el) => {
        // Apenas se ainda não estiver dentro de um data-card-form
        if (!el.closest('[data-card-form]')) (el as HTMLElement).dataset.cardForm = 'true'
      })
      // Acrescenta estados (não remove o form)
      const cardStates = document.createElement('div')
      cardStates.innerHTML = `
        <div class="card-state-loading pix-state-grid">
          <div class="pix-loading-spin"></div>
          <strong>Processando pagamento…</strong>
          <p>Não feche essa janela. Estamos confirmando seu cartão com o banco.</p>
        </div>
        <div class="card-state-success pix-state-grid">
          <div class="success-check">✓</div>
          <strong>Pagamento aprovado!</strong>
          <p data-card-success-msg>Você receberá o e-mail de confirmação em alguns instantes. Acesso ao conteúdo digital liberado.</p>
        </div>
        <div class="card-state-error pix-state-grid">
          <div class="error-x">×</div>
          <strong style="color:#dc2626">Pagamento recusado</strong>
          <p data-card-error-msg>Verifique os dados do cartão e tente novamente. Se persistir, use outro cartão, PIX ou boleto.</p>
          <button type="button" class="checkout-btn-secondary" data-card-retry>Tentar de novo</button>
        </div>
      `
      while (cardStates.firstChild) cardPanel.appendChild(cardStates.firstChild)
      cardPanel.dataset.state = 'idle'
    }

    const onCardRetry = () => {
      if (cardPanel) cardPanel.dataset.state = 'idle'
    }
    const cardRetryBtn = cardPanel?.querySelector<HTMLButtonElement>('[data-card-retry]')
    cardRetryBtn?.addEventListener('click', onCardRetry)

    // ==================================================================
    // BOLETO transparente — linha digitável + link PDF
    // ==================================================================
    const boletoPanel = document.querySelector<HTMLElement>('.payment-panel[data-panel="boleto"]')
    const boletoInfo = boletoPanel?.querySelector<HTMLElement>('.boleto-info')
    let boletoOriginalHTML = ''
    if (boletoInfo) {
      boletoOriginalHTML = boletoInfo.innerHTML
      boletoInfo.dataset.state = 'idle'
      boletoInfo.innerHTML = `
        <div class="boleto-state-idle">${boletoOriginalHTML}</div>
        <div class="boleto-state-loading pix-state-grid">
          <div class="pix-loading-spin"></div>
          <strong>Gerando boleto…</strong>
          <p>Aguarde alguns segundos, seu boleto está sendo emitido.</p>
        </div>
        <div class="boleto-state-ready pix-state-grid">
          <strong>Boleto gerado com sucesso</strong>
          <p>Pague em qualquer banco, lotérica ou app bancário. Compensação em até 2 dias úteis.</p>
          <a data-boleto-pdf href="#" target="_blank" rel="noopener" class="checkout-btn-primary" style="text-decoration:none">Abrir boleto (PDF)</a>
          <p style="font-size:13px;color:var(--fg2,#555);margin-top:6px">Ou copie a linha digitável:</p>
          <div class="pix-copy-row" style="width:100%;max-width:480px;">
            <input type="text" readonly data-boleto-line value="" aria-label="Linha digitável do boleto" />
            <button type="button" data-boleto-copy>Copiar</button>
          </div>
          <p data-boleto-expiration style="font-size:12px;color:var(--fg3,#888)"></p>
        </div>
        <div class="boleto-state-error pix-state-grid">
          <strong style="color:#dc2626">Não foi possível gerar o boleto</strong>
          <p data-boleto-error-msg>Tente novamente ou use PIX/cartão.</p>
          <button type="button" class="checkout-btn-secondary" data-boleto-retry>Tentar de novo</button>
        </div>
      `
    }

    let boletoLastPaymentId: string | null = null

    async function loadBoleto(paymentId: string) {
      if (!boletoInfo) return
      boletoInfo.dataset.state = 'loading'
      try {
        const r = await fetch(`/api/checkout/boleto/${encodeURIComponent(paymentId)}`, {
          headers: { Accept: 'application/json' },
        })
        const data = await r.json()
        if (!r.ok || !data.identificationField) {
          throw new Error(data?.error || 'falha_gerar_boleto')
        }
        const lineInput = boletoInfo.querySelector<HTMLInputElement>('[data-boleto-line]')
        const pdfLink = boletoInfo.querySelector<HTMLAnchorElement>('[data-boleto-pdf]')
        const expSpan = boletoInfo.querySelector<HTMLElement>('[data-boleto-expiration]')
        if (lineInput) lineInput.value = data.identificationField
        if (pdfLink && data.bankSlipUrl) pdfLink.href = data.bankSlipUrl
        if (expSpan && data.dueDate) {
          try {
            const d = new Date(data.dueDate)
            expSpan.textContent = `Vence em ${d.toLocaleDateString('pt-BR')}`
          } catch {}
        }
        boletoInfo.dataset.state = 'ready'
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'erro'
        const errSpan = boletoInfo.querySelector<HTMLElement>('[data-boleto-error-msg]')
        if (errSpan) errSpan.textContent = `Erro: ${errMsg}. Tente novamente ou use PIX/cartão.`
        boletoInfo.dataset.state = 'error'
      }
    }

    const onBoletoCopyClick = async (e: Event) => {
      const btn = (e.currentTarget as HTMLButtonElement)
      const input = boletoInfo?.querySelector<HTMLInputElement>('[data-boleto-line]')
      if (!input || !input.value) return
      try {
        await navigator.clipboard.writeText(input.value)
        const orig = btn.textContent
        btn.classList.add('copied')
        btn.textContent = 'Copiado!'
        setTimeout(() => { btn.classList.remove('copied'); btn.textContent = orig || 'Copiar' }, 2000)
      } catch {
        input.select()
        try { document.execCommand('copy') } catch {}
      }
    }
    const boletoCopyBtn = boletoInfo?.querySelector<HTMLButtonElement>('[data-boleto-copy]')
    boletoCopyBtn?.addEventListener('click', onBoletoCopyClick)

    const onBoletoRetry = () => { if (boletoLastPaymentId) loadBoleto(boletoLastPaymentId) }
    const boletoRetryBtn = boletoInfo?.querySelector<HTMLButtonElement>('[data-boleto-retry]')
    boletoRetryBtn?.addEventListener('click', onBoletoRetry)

    let pixLastPaymentId: string | null = null

    async function loadPixQrCode(paymentId: string) {
      if (!pixDisplay) return
      pixDisplay.dataset.state = 'loading'
      try {
        const r = await fetch(`/api/checkout/pix-qrcode/${encodeURIComponent(paymentId)}`, {
          headers: { Accept: 'application/json' },
        })
        const data = await r.json()
        if (!r.ok || !data.encodedImage || !data.payload) {
          throw new Error(data?.error || 'falha_gerar_pix')
        }
        const img = pixDisplay.querySelector<HTMLImageElement>('[data-pix-qr]')
        const payloadInput = pixDisplay.querySelector<HTMLInputElement>('[data-pix-payload]')
        const expSpan = pixDisplay.querySelector<HTMLElement>('[data-pix-expiration]')
        if (img) img.src = `data:image/png;base64,${data.encodedImage}`
        if (payloadInput) payloadInput.value = data.payload
        if (expSpan && data.expirationDate) {
          try {
            const d = new Date(data.expirationDate)
            expSpan.textContent = `Válido até ${d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
          } catch {}
        }
        pixDisplay.dataset.state = 'ready'
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'erro'
        const errSpan = pixDisplay.querySelector<HTMLElement>('[data-pix-error-msg]')
        if (errSpan) errSpan.textContent = `Erro: ${errMsg}. Tente novamente ou use outra forma.`
        pixDisplay.dataset.state = 'error'
      }
    }

    // Botão copiar
    const onPixCopyClick = async (e: Event) => {
      const btn = (e.currentTarget as HTMLButtonElement)
      const input = pixDisplay?.querySelector<HTMLInputElement>('[data-pix-payload]')
      if (!input || !input.value) return
      try {
        await navigator.clipboard.writeText(input.value)
        const orig = btn.textContent
        btn.classList.add('copied')
        btn.textContent = 'Copiado!'
        setTimeout(() => {
          btn.classList.remove('copied')
          btn.textContent = orig || 'Copiar'
        }, 2000)
      } catch {
        input.select()
        try { document.execCommand('copy') } catch {}
      }
    }
    const copyBtn = pixDisplay?.querySelector<HTMLButtonElement>('[data-pix-copy]')
    copyBtn?.addEventListener('click', onPixCopyClick)

    // Botão "tentar de novo"
    const onPixRetry = () => {
      if (pixLastPaymentId) loadPixQrCode(pixLastPaymentId)
    }
    const retryBtn = pixDisplay?.querySelector<HTMLButtonElement>('[data-pix-retry]')
    retryBtn?.addEventListener('click', onPixRetry)

    // Submit handler: intercepta SEMPRE (PIX/CARD/BOLETO transparentes)
    const onSubmit = async (e: Event) => {
      const billingType = (billingHidden?.value || 'PIX').toUpperCase()
      e.preventDefault()

      // Validação HTML5 nativa
      if (!form.checkValidity()) {
        form.reportValidity()
        return
      }

      // Para cartão: validações extras de cartão antes do submit
      if (billingType === 'CREDIT_CARD') {
        const cardNumber = (form.querySelector<HTMLInputElement>('input[name="cardNumber"]')?.value || '').replace(/\s/g, '')
        const cardExp = form.querySelector<HTMLInputElement>('input[name="cardExp"]')?.value || ''
        const cardCvv = form.querySelector<HTMLInputElement>('input[name="cardCvv"]')?.value || ''
        const cardName = form.querySelector<HTMLInputElement>('input[name="cardName"]')?.value || ''
        if (!/^\d{13,19}$/.test(cardNumber)) { alert('Número de cartão inválido.'); return }
        if (!/^\d{2}\/\d{2,4}$/.test(cardExp)) { alert('Validade do cartão inválida (use MM/AA).'); return }
        if (!/^\d{3,4}$/.test(cardCvv)) { alert('CVV inválido.'); return }
        if (cardName.trim().length < 3) { alert('Nome no cartão inválido.'); return }
      }

      // Scroll + estado loading
      const targetEl =
        billingType === 'PIX' ? pixDisplay :
        billingType === 'BOLETO' ? boletoInfo :
        cardPanel
      if (targetEl) {
        targetEl.dataset.state = 'loading'
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      try {
        const formData = new FormData(form)
        const action = form.getAttribute('action') || ''
        const res = await fetch(action, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        })
        const data = await res.json()
        if (!res.ok || !data.payment_id) {
          throw new Error(data?.detail || data?.error || 'falha_cobranca')
        }

        if (billingType === 'PIX') {
          pixLastPaymentId = data.payment_id
          await loadPixQrCode(data.payment_id)
        } else if (billingType === 'BOLETO') {
          boletoLastPaymentId = data.payment_id
          await loadBoleto(data.payment_id)
        } else {
          // CREDIT_CARD: o status já vem na resposta
          const status = String(data.status || '').toUpperCase()
          if (cardPanel) {
            if (status === 'CONFIRMED' || status === 'RECEIVED' || status === 'AUTHORIZED') {
              cardPanel.dataset.state = 'success'
            } else if (status === 'PENDING') {
              const msg = cardPanel.querySelector<HTMLElement>('[data-card-success-msg]')
              if (msg) msg.textContent = 'Recebemos seu pagamento. A confirmação chega em alguns minutos por e-mail e o acesso é liberado automaticamente.'
              cardPanel.dataset.state = 'success'
            } else {
              const errMsg = cardPanel.querySelector<HTMLElement>('[data-card-error-msg]')
              if (errMsg) errMsg.textContent = `Cartão recusado (${status || 'erro'}). Verifique os dados ou use outro cartão, PIX ou boleto.`
              cardPanel.dataset.state = 'error'
            }
          }
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'desconhecido'
        if (billingType === 'PIX' && pixDisplay) {
          const errSpan = pixDisplay.querySelector<HTMLElement>('[data-pix-error-msg]')
          if (errSpan) errSpan.textContent = `Erro: ${errMsg}.`
          pixDisplay.dataset.state = 'error'
        } else if (billingType === 'BOLETO' && boletoInfo) {
          const errSpan = boletoInfo.querySelector<HTMLElement>('[data-boleto-error-msg]')
          if (errSpan) errSpan.textContent = `Erro: ${errMsg}.`
          boletoInfo.dataset.state = 'error'
        } else if (cardPanel) {
          const errSpan = cardPanel.querySelector<HTMLElement>('[data-card-error-msg]')
          if (errSpan) errSpan.textContent = `Erro: ${errMsg}. Tente novamente ou use outra forma.`
          cardPanel.dataset.state = 'error'
        }
      }
    }
    form.addEventListener('submit', onSubmit)

    // ====================================================================
    // CLEANUP
    // ====================================================================
    return () => {
      planRadios.forEach((r) => r.removeEventListener('change', onPlanChange))
      labels.forEach((l) => l.removeEventListener('click', onLabelClick))
      cepInput?.removeEventListener('blur', onCepBlur)
      cepInput?.removeEventListener('input', onCepInput)
      payTabs.forEach((b) => b.removeEventListener('click', onPayTab))
      copyBtn?.removeEventListener('click', onPixCopyClick)
      retryBtn?.removeEventListener('click', onPixRetry)
      cardNumberInput?.removeEventListener('input', onCardNumberInput)
      cardExpInput?.removeEventListener('input', onCardExpInput)
      cardCvvInput?.removeEventListener('input', onCardCvvInput)
      cardRetryBtn?.removeEventListener('click', onCardRetry)
      boletoCopyBtn?.removeEventListener('click', onBoletoCopyClick)
      boletoRetryBtn?.removeEventListener('click', onBoletoRetry)
      form.removeEventListener('submit', onSubmit)
      styleEl.remove()
    }
  }, [])

  return null
}
